import type { FC, PropsWithChildren } from 'react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { ConfigProvider, theme as antdTheme } from 'antd'
import type { Locale } from 'antd/es/locale'
import ptBR from 'antd/locale/pt_BR'
import { DiferpanThemeProvider } from 'diferpan-common-web'
import {
  applyColorsToRoot,
  applyFixedZebra,
  componentsForTheme,
  getThemeColors,
  ThemeName,
  tokensFromColors,
} from '@theme'

interface ThemeCtx { theme: ThemeName; setTheme: (t: ThemeName) => void; toggle: () => void }
const ThemeContext = createContext<ThemeCtx | null>(null)

type ThemeProviderProps = PropsWithChildren<{
  locale?: Locale
}>

export const ThemeProvider: FC<ThemeProviderProps> = ({ children, locale }) => {
  const hasWindow = typeof window !== 'undefined'
  const prefersDark = hasWindow && window.matchMedia?.('(prefers-color-scheme: dark)').matches

  const initialTheme: ThemeName =
    (hasWindow ? (localStorage.getItem('theme') as ThemeName | null) : null) ?? (prefersDark ? 'dark' : 'light')

  const [theme, setTheme] = useState<ThemeName>(initialTheme)

  useEffect(() => {
    const vars = getThemeColors(theme)
    applyColorsToRoot(vars)
    applyFixedZebra(theme)

    if (hasWindow) {
      document.documentElement.setAttribute('data-theme', theme)
      localStorage.setItem('theme', theme)
    }
  }, [theme, hasWindow])

  const vars = useMemo(() => getThemeColors(theme), [theme])
  const tokens = useMemo(() => tokensFromColors(vars), [vars])
  const algorithm = theme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm
  const components = useMemo(() => componentsForTheme(theme), [theme])

  return (
    <ThemeContext
      value={{ theme, setTheme, toggle: () => setTheme((t) => (t === 'dark' ? 'light' : 'dark')) }}
    >
      <ConfigProvider locale={locale ?? ptBR} theme={{ token: tokens, algorithm, components }}>
        <DiferpanThemeProvider theme={theme} legacyBridge={false}>
          {children}
        </DiferpanThemeProvider>
      </ConfigProvider>
    </ThemeContext>
  )
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
